import WorkAgreement from "work/work-agreements/work-agreement";
import Proposal from "work/proposals/proposal";
import Guid from "_shared/guids/guid";
import { Resource } from "permissions/resources/permission-resource";
import WorkAgreementsAccessSchema, {
  WorkAgreementsAccessJSON,
  WorkAgreementsAccessParams
} from "permissions/resources/work/_schemas/work-agreements-access-schema";
import {
  AccessLevel,
  AnyResourceJSON,
  ResourceFromJSON,
  ResourceType,
  Scope
} from "permissions/resources/types";
import ProposalsAccessSchema, {
  ProposalsAccessJSON,
  ProposalsAccessParams
} from "permissions/resources/work/_schemas/proposals-access-schema";
import TeamsAccessSchema, { TeamsAccessJSON, TeamsAccessParams } from "permissions/resources/work/_schemas/teams-access-schema";
import ContractsAccessSchema, { ContractsAccessJSON, ContractsAccessParams } from "permissions/resources/work/_schemas/contracts-access-schema";
import FeeSchedulesAccessSchema, { FeeSchedulesAccessJSON, FeeSchedulesAccessParams } from "permissions/resources/work/_schemas/fee-schedules-access-schema";
import CurrencyFieldsAccessSchema, { CurrencyFieldsAccessJSON, CurrencyFieldsAccessParams } from "permissions/resources/work/_schemas/currency-fields-access-schema";
import DiscountsAccessSchema, { DiscountsAccessJSON, DiscountsAccessParams } from "permissions/resources/work/_schemas/discounts-access-schema";
import InvoicesAccessSchema, { InvoicesAccessJSON, InvoicesAccessParams } from "permissions/resources/work/_schemas/invoices-access-schema";
import { WorkAgreementField } from "permissions/resources/work/types";
import ProposalAccessSchema, { ProposalAccessJSON, ProposalAccessParams } from "permissions/resources/work/_schemas/proposal-access-schema";
import WorkAgreementAccessSchema, { WorkAgreementAccessJSON, WorkAgreementAccessParams } from "permissions/resources/work/_schemas/work-agreement-access-schema";
import ProposalRequestAccessSchema, { ProposalRequestAccessJSON, ProposalRequestAccessParams } from "permissions/resources/work/_schemas/proposal-request-access-schema";
import FeeScheduleTemplateAccessSchema, { FeeScheduleTemplateAccessJSON, FeeScheduleTemplateAccessParams } from "permissions/resources/work/_schemas/fee-schedule-template-access-schema";
import TeamTemplateAccessSchema, { TeamTemplateAccessJSON, TeamTemplateAccessParams } from "permissions/resources/work/_schemas/team-template-access-schema";
import { registerResource } from "permissions/resources/resource-registry";

export class WorkAgreementsAccess extends Resource {
  public static create(params?: WorkAgreementsAccessParams): WorkAgreementsAccess {
    const parsed = WorkAgreementsAccessSchema.validate(params);
    return new WorkAgreementsAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const parsed = WorkAgreementsAccessSchema.deserialize(json as WorkAgreementsAccessJSON);
    return new WorkAgreementsAccess(parsed) as ResourceFromJSON<J>;
  }

  public static getPermutations(): WorkAgreementsAccess[] {
    const fieldIterations: [key: string, value: number[]][] = [
      ['accessLevel', Object.values(AccessLevel).filter((key) => !Number.isNaN(Number(key))).map(value => Number(value))],
      ['scope', Object.values(Scope).filter((key) => !Number.isNaN(Number(key))).map(value => Number(value))],
    ];
    return this.generatePermutations(WorkAgreementsAccess, fieldIterations) as WorkAgreementsAccess[];
  }

  public readonly resourceType = ResourceType.WorkAgreementsAccess;
  public readonly accessLevel?: AccessLevel;
  public readonly scope?: Scope;

  public constructor(params?: WorkAgreementsAccessParams) {
    super(params);

    if (params) {
      this.accessLevel = params.accessLevel;
      this.scope = params.scope;
    }
  }

  public toJSON(): WorkAgreementsAccessJSON {
    return WorkAgreementsAccessSchema.serialize(this);
  }

  public override toString(): string {
    return `WorkAgreementsAccess {
      resourceType: ${this.resourceType}
      isDisabled: ${this.isDisabled ? 'true' : 'false'}
      resourceTypeText: ${this.resourceTypeText}
      accessLevel: ${this.accessLevel}
      scope: ${this.scope}
    }`
  }
}

registerResource("WorkAgreementsAccess", (json) => WorkAgreementsAccess.fromJSON(json));

export class ProposalsAccess extends Resource {
  public static create(params?: ProposalsAccessParams): ProposalsAccess {
    const parsed = ProposalsAccessSchema.validate(params);
    return new ProposalsAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const parsed = ProposalsAccessSchema.deserialize(json as ProposalsAccessJSON);
    return new ProposalsAccess(parsed) as ResourceFromJSON<J>;
  }
  public static getPermutations(): ProposalsAccess[] {
    const fieldIterations: [key: string, value: number[]][] = [
      ['accessLevel', Object.values(AccessLevel).filter((key) => !Number.isNaN(Number(key))).map(value => Number(value))],
      ['scope', Object.values(Scope).filter((key) => !Number.isNaN(Number(key))).map(value => Number(value))],
    ];
    return this.generatePermutations(ProposalsAccess, fieldIterations) as ProposalsAccess[];
  }

  public readonly resourceType = ResourceType.ProposalsAccess;
  public readonly accessLevel?: AccessLevel;
  public readonly scope?: Scope;

  public constructor(params?: ProposalsAccessParams) {
    super(params);

    if (params) {
      this.accessLevel = params.accessLevel;
      this.scope = params.scope;
    }
  }

  public toJSON(): ProposalsAccessJSON {
    return ProposalsAccessSchema.serialize(this);
  }

  public override toString(): string {
    return `ProposalsAccess {
      resourceType: ${this.resourceType}
      isDisabled: ${this.isDisabled ? 'true' : 'false'}
      resourceTypeText: ${this.resourceTypeText}
      accessLevel: ${this.accessLevel}
      scope: ${this.scope}
    }`
  }
}

registerResource("ProposalsAccess", (json) => ProposalsAccess.fromJSON(json));

export class ProposalRequestAccess extends Resource {
  public static create(params?: ProposalRequestAccessParams): ProposalRequestAccess {
    const parsed = ProposalRequestAccessSchema.validate(params);
    return new ProposalRequestAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const parsed = ProposalRequestAccessSchema.deserialize(json as ProposalRequestAccessJSON);
    return new ProposalRequestAccess(parsed) as ResourceFromJSON<J>;
  }

  public readonly resourceType = ResourceType.ProposalRequestAccess;
  public readonly requestId?: Guid;
  public readonly accessLevel?: AccessLevel;

  public constructor(params?: ProposalRequestAccessParams) {
    super(params);

    if (params) {
      this.accessLevel = params.accessLevel;
      this.requestId = params.requestId;
    }
  }

  public toJSON(): ProposalRequestAccessJSON {
    return ProposalRequestAccessSchema.serialize(this);
  }

  public override toString(): string {
    return `ProposalRequestAccess {
      resourceType: ${this.resourceType}
      isDisabled: ${this.isDisabled ? 'true' : 'false'}
      resourceTypeText: ${this.resourceTypeText}
      requestId: ${this.requestId}
      accessLevel: ${this.accessLevel}
    }`
  }
}

registerResource("ProposalRequestAccess", (json) => ProposalRequestAccess.fromJSON(json));

export class TeamsAccess extends Resource {
  public static create(params?: TeamsAccessParams): TeamsAccess {
    const parsed = TeamsAccessSchema.validate(params);
    return new TeamsAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const parsed = TeamsAccessSchema.deserialize(json as TeamsAccessJSON);
    return new TeamsAccess(parsed) as ResourceFromJSON<J>;
  }

  public static getPermutations(): TeamsAccess[] {
    const fieldIterations: [key: string, value: number[]][] = [
      ['accessLevel', Object.values(AccessLevel).filter((key) => !Number.isNaN(Number(key))).map(value => Number(value))],
      ['scope', Object.values(Scope).filter((key) => !Number.isNaN(Number(key))).map(value => Number(value))],
    ];
    return this.generatePermutations(TeamsAccess, fieldIterations) as TeamsAccess[];
  }

  public readonly resourceType = ResourceType.TeamsAccess;
  public readonly accessLevel?: AccessLevel;
  public readonly scope?: Scope;

  public constructor(params?: TeamsAccessParams) {
    super(params);

    if (params) {
      this.accessLevel = params.accessLevel;
      this.scope = params.scope;
    }
  }

  public toJSON(): TeamsAccessJSON {
    return TeamsAccessSchema.serialize(this);
  }

  public override toString(): string {
    return `TeamsAccess {
      resourceType: ${this.resourceType}
      isDisabled: ${this.isDisabled ? 'true' : 'false'}
      resourceTypeText: ${this.resourceTypeText}
      accessLevel: ${this.accessLevel}
      scope: ${this.scope}
    }`
  }
}

registerResource("TeamsAccess", (json) => TeamsAccess.fromJSON(json));

export class TeamTemplateAccess extends Resource {
  public static create(params?: TeamTemplateAccessParams): TeamTemplateAccess {
    const parsed = TeamTemplateAccessSchema.validate(params);
    return new TeamTemplateAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const parsed = TeamTemplateAccessSchema.deserialize(json as TeamTemplateAccessJSON);
    return new TeamTemplateAccess(parsed) as ResourceFromJSON<J>;
  }

  public readonly resourceType = ResourceType.TeamTemplateAccess;
  public readonly templateId?: Guid;
  public readonly accessLevel?: AccessLevel;

  public constructor(params?: TeamTemplateAccessParams) {
    super(params);

    if (params) {
      this.accessLevel = params.accessLevel;
      this.templateId = params.templateId;
    }
  }

  public toJSON(): TeamTemplateAccessJSON {
    return TeamTemplateAccessSchema.serialize(this);
  }

  public override toString(): string {
    return `TeamTemplateAccess {
      resourceType: ${this.resourceType}
      isDisabled: ${this.isDisabled ? 'true' : 'false'}
      resourceTypeText: ${this.resourceTypeText}
      templateId: ${this.templateId}
      accessLevel: ${this.accessLevel}
    }`
  }
}

registerResource("TeamTemplateAccess", (json) => TeamTemplateAccess.fromJSON(json));

export class ContractsAccess extends Resource {
  public static create(params?: ContractsAccessParams): ContractsAccess {
    const parsed = ContractsAccessSchema.validate(params);
    return new ContractsAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const parsed = ContractsAccessSchema.deserialize(json as ContractsAccessJSON);
    return new ContractsAccess(parsed) as ResourceFromJSON<J>;
  }

  public static getPermutations(): ContractsAccess[] {
    const fieldIterations: [key: string, value: number[]][] = [
      ['accessLevel', Object.values(AccessLevel).filter((key) => !Number.isNaN(Number(key))).map(value => Number(value))],
      ['scope', Object.values(Scope).filter((key) => !Number.isNaN(Number(key))).map(value => Number(value))],
    ];
    return this.generatePermutations(ContractsAccess, fieldIterations) as ContractsAccess[];
  }

  public readonly resourceType = ResourceType.ContractsAccess;
  public readonly accessLevel?: AccessLevel;
  public readonly scope?: Scope;

  public constructor(params?: ContractsAccessParams) {
    super(params);

    if (params) {
      this.accessLevel = params.accessLevel;
      this.scope = params.scope;
    }
  }

  public toJSON(): ContractsAccessJSON {
    return ContractsAccessSchema.serialize(this);
  }

  public override toString(): string {
    return `ContractsAccess {
      resourceType: ${this.resourceType}
      isDisabled: ${this.isDisabled ? 'true' : 'false'}
      resourceTypeText: ${this.resourceTypeText}
      accessLevel: ${this.accessLevel}
      scope: ${this.scope}
    }`
  }
}

registerResource("ContractsAccess", (json) => ContractsAccess.fromJSON(json));

export class FeeSchedulesAccess extends Resource {
  public static create(params?: FeeSchedulesAccessParams): FeeSchedulesAccess {
    const parsed = FeeSchedulesAccessSchema.validate(params);
    return new FeeSchedulesAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const parsed = FeeSchedulesAccessSchema.deserialize(json as FeeSchedulesAccessJSON);
    return new FeeSchedulesAccess(parsed) as ResourceFromJSON<J>;
  }

  public static getPermutations(): FeeSchedulesAccess[] {
    const fieldIterations: [key: string, value: number[]][] = [
      ['accessLevel', Object.values(AccessLevel).filter((key) => !Number.isNaN(Number(key))).map(value => Number(value))],
      ['scope', Object.values(Scope).filter((key) => !Number.isNaN(Number(key))).map(value => Number(value))],
    ];
    return this.generatePermutations(FeeSchedulesAccess, fieldIterations) as FeeSchedulesAccess[];
  }

  public readonly resourceType = ResourceType.FeeSchedulesAccess;
  public readonly accessLevel?: AccessLevel;
  public scope?: Scope;

  public constructor(params?: FeeSchedulesAccessParams) {
    super(params);

    if (params) {
      this.accessLevel = params.accessLevel;
      this.scope = params.scope;
    }
  }

  public toJSON(): FeeSchedulesAccessJSON {
    return FeeSchedulesAccessSchema.serialize(this);
  }

  public override toString(): string {
    return `FeeSchedulesAccess {
      resourceType: ${this.resourceType}
      isDisabled: ${this.isDisabled ? 'true' : 'false'}
      resourceTypeText: ${this.resourceTypeText}
      accessLevel: ${this.accessLevel}
      scope: ${this.scope}
    }`
  }
}

registerResource("FeeSchedulesAccess", (json) => FeeSchedulesAccess.fromJSON(json));

export class FeeScheduleTemplateAccess extends Resource {
  public static create(params?: FeeScheduleTemplateAccessParams): FeeScheduleTemplateAccess {
    const parsed = FeeScheduleTemplateAccessSchema.validate(params);
    return new FeeScheduleTemplateAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const parsed = FeeScheduleTemplateAccessSchema.deserialize(json as FeeScheduleTemplateAccessJSON);
    return new FeeScheduleTemplateAccess(parsed) as ResourceFromJSON<J>;
  }

  public readonly resourceType = ResourceType.FeeScheduleTemplateAccess;
  public readonly templateId?: Guid;
  public readonly accessLevel?: AccessLevel;

  public constructor(params?: FeeScheduleTemplateAccessParams) {
    super(params);

    if (params) {
      this.accessLevel = params.accessLevel;
      this.templateId = params.templateId;
    }
  }

  public toJSON(): FeeScheduleTemplateAccessJSON {
    return FeeScheduleTemplateAccessSchema.serialize(this);
  }

  public override toString(): string {
    return `FeeScheduleTemplateAccess {
      resourceType: ${this.resourceType}
      isDisabled: ${this.isDisabled ? 'true' : 'false'}
      resourceTypeText: ${this.resourceTypeText}
      templateId: ${this.templateId}
      accessLevel: ${this.accessLevel}
    }`
  }
}

registerResource("FeeScheduleTemplateAccess", (json) => FeeScheduleTemplateAccess.fromJSON(json));

export class CurrencyFieldsAccess extends Resource {
  public static create(params?: CurrencyFieldsAccessParams): CurrencyFieldsAccess {
    const parsed = CurrencyFieldsAccessSchema.validate(params);
    return new CurrencyFieldsAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const parsed = CurrencyFieldsAccessSchema.deserialize(json as CurrencyFieldsAccessJSON);
    return new CurrencyFieldsAccess(parsed) as ResourceFromJSON<J>;
  }

  public static getPermutations(): CurrencyFieldsAccess[] {
    const fieldIterations: [key: string, value: number[]][] = [
      ['accessLevel', Object.values(AccessLevel).filter((key) => !Number.isNaN(Number(key))).map(value => Number(value))],
      ['scope', Object.values(Scope).filter((key) => !Number.isNaN(Number(key))).map(value => Number(value))],
    ];
    return this.generatePermutations(CurrencyFieldsAccess, fieldIterations) as CurrencyFieldsAccess[];
  }

  public readonly resourceType = ResourceType.CurrencyFieldsAccess;
  public readonly accessLevel?: AccessLevel;
  public readonly scope?: Scope;

  public constructor(params?: CurrencyFieldsAccessParams) {
    super(params);

    if (params) {
      this.accessLevel = params.accessLevel;
      this.scope = params.scope;
    }
  }

  public toJSON(): CurrencyFieldsAccessJSON {
    return CurrencyFieldsAccessSchema.serialize(this);
  }

  public override toString(): string {
    return `CurrencyFieldsAccess {
      resourceType: ${this.resourceType}
      isDisabled: ${this.isDisabled ? 'true' : 'false'}
      resourceTypeText: ${this.resourceTypeText}
      accessLevel: ${this.accessLevel}
      scope: ${this.scope}
    }`
  }
}

registerResource("CurrencyFieldsAccess", (json) => CurrencyFieldsAccess.fromJSON(json));

export class DiscountsAccess extends Resource {
  public static create(params?: DiscountsAccessParams): DiscountsAccess {
    const parsed = DiscountsAccessSchema.validate(params);
    return new DiscountsAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const parsed = DiscountsAccessSchema.deserialize(json as DiscountsAccessJSON);
    return new DiscountsAccess(parsed) as ResourceFromJSON<J>;
  }

  public static getPermutations(): DiscountsAccess[] {
    const fieldIterations: [key: string, value: number[]][] = [
      ['accessLevel', Object.values(AccessLevel).filter((key) => !Number.isNaN(Number(key))).map(value => Number(value))],
      ['scope', Object.values(Scope).filter((key) => !Number.isNaN(Number(key))).map(value => Number(value))],
    ];
    return this.generatePermutations(DiscountsAccess, fieldIterations) as DiscountsAccess[];
  }

  public readonly resourceType = ResourceType.DiscountsAccess;
  public readonly accessLevel?: AccessLevel;
  public readonly scope?: Scope;

  public constructor(params?: DiscountsAccessParams) {
    super(params);

    if (params) {
      this.accessLevel = params.accessLevel;
      this.scope = params.scope;
    }
  }

  public toJSON(): DiscountsAccessJSON {
    return DiscountsAccessSchema.serialize(this);
  }

  public override toString(): string {
    return `DiscountsAccess {
      resourceType: ${this.resourceType}
      isDisabled: ${this.isDisabled ? 'true' : 'false'}
      resourceTypeText: ${this.resourceTypeText}
      accessLevel: ${this.accessLevel}
      scope: ${this.scope}
    }`
  }
}

registerResource("DiscountsAccess", (json) => DiscountsAccess.fromJSON(json));

export class InvoicesAccess extends Resource {
  public static create(params?: InvoicesAccessParams): InvoicesAccess {
    const parsed = InvoicesAccessSchema.validate(params);
    return new InvoicesAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const parsed = InvoicesAccessSchema.deserialize(json as InvoicesAccessJSON);
    return new InvoicesAccess(parsed) as ResourceFromJSON<J>;
  }

  public static getPermutations(): InvoicesAccess[] {
    const fieldIterations: [key: string, value: number[]][] = [
      ['accessLevel', Object.values(AccessLevel).filter((key) => !Number.isNaN(Number(key))).map(value => Number(value))],
      ['scope', Object.values(Scope).filter((key) => !Number.isNaN(Number(key))).map(value => Number(value))],
    ];
    return this.generatePermutations(InvoicesAccess, fieldIterations) as InvoicesAccess[];
  }

  public readonly resourceType = ResourceType.InvoicesAccess;
  public readonly accessLevel?: AccessLevel;
  public readonly scope?: Scope;

  public constructor(params?: InvoicesAccessParams) {
    super(params);

    if (params) {
      this.accessLevel = params.accessLevel;
      this.scope = params.scope;
    }
  }

  public toJSON(): InvoicesAccessJSON {
    return InvoicesAccessSchema.serialize(this);
  }

  public override toString(): string {
    return `InvoicesAccess {
      resourceType: ${this.resourceType}
      isDisabled: ${this.isDisabled ? 'true' : 'false'}
      resourceTypeText: ${this.resourceTypeText}
      accessLevel: ${this.accessLevel}
      scope: ${this.scope}
    }`
  }
}

registerResource("InvoicesAccess", (json) => InvoicesAccess.fromJSON(json));

export class ProposalAccess extends Resource {
  public static create(params?: ProposalAccessParams): ProposalAccess {
    const parsed = ProposalAccessSchema.validate(params);
    return new ProposalAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const parsed = ProposalAccessSchema.deserialize(json as ProposalAccessJSON);
    return new ProposalAccess(parsed) as ResourceFromJSON<J>;
  }

  public readonly resourceType = ResourceType.ProposalAccess;
  public readonly proposalId?: Guid;
  public readonly fields?: Record<WorkAgreementField, AccessLevel>;

  public constructor(params?: Omit<ProposalAccessParams, 'resourceType'>) {
    super(params);
    if (params) {
      this.proposalId = params.proposalId;
      this.fields = params.fields;
    }
  }

  public toJSON(): ProposalAccessJSON {
    return ProposalAccessSchema.serialize(this);
  }

  public override toString(options: {
    proposal: Proposal;
    isLoadingProposal: boolean | undefined;
    errorLoadingProposal: string | null | undefined;
  }): string {
    if (options?.isLoadingProposal) {
      return `Work Proposal: Loading...`;
    }

    if (options?.errorLoadingProposal) {
      return `Work Proposal: ${this.proposalId?.value} - Error: ${options.errorLoadingProposal}`;
    }

    return `ProposalAccess { 
      ${options.proposal.name}: ${JSON.stringify(this.fields)}
    }`;
  }
}

registerResource("ProposalAccess", (json) => ProposalAccess.fromJSON(json));

export class WorkAgreementAccess extends Resource {
  public static create(params?: WorkAgreementAccessParams): WorkAgreementAccess {
    const parsed = WorkAgreementAccessSchema.validate(params);
    return new WorkAgreementAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const parsed = WorkAgreementAccessSchema.deserialize(json as WorkAgreementAccessJSON);
    return new WorkAgreementAccess(parsed) as ResourceFromJSON<J>;
  }

  public readonly resourceType = ResourceType.WorkAgreementAccess;
  public readonly workAgreementId?: Guid;
  public readonly fields?: Record<WorkAgreementField, AccessLevel>;

  public constructor(params?: Omit<WorkAgreementAccessParams, 'resourceType'>) {
    super(params);
    if (params) {
      this.workAgreementId = params.workAgreementId;
      this.fields = params.fields;
    }
  }

  public toJSON(): WorkAgreementAccessJSON {
    return WorkAgreementAccessSchema.serialize(this);
  }

  public override toString(options: {
    workAgreement: WorkAgreement;
    isLoadingWorkAgreement: boolean | undefined;
    errorLoadingWorkAgreement: string | null | undefined;
  }): string {
    if (options?.isLoadingWorkAgreement) {
      return `Work WorkAgreement: Loading...`;
    }

    if (options?.errorLoadingWorkAgreement) {
      return `Work WorkAgreement: ${this.workAgreementId?.value} - Error: ${options.errorLoadingWorkAgreement}`;
    }

    return `WorkAgreementAccess { 
      ${options.workAgreement.name}: ${JSON.stringify(this.fields)}
    }`;
  }
}

registerResource("WorkAgreementAccess", (json) => WorkAgreementAccess.fromJSON(json));
